import React, { useEffect, useState } from 'react';
import { Container, Card, Button, Row, Col, Form, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageLoader } from '../context/PageLoaderContext';
import ProductCard from '../components/product/ProductCard';
import './Cart.css';

const Cart = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { setPageLoading } = usePageLoader();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bestsellers, setBestsellers] = useState([]);

  useEffect(() => {
    setPageLoading(loading);
    return () => setPageLoading(false);
  }, [loading, setPageLoading]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    }
    fetchBestsellers();
  }, [isAuthenticated]);

  // Refetch cart when items are added/removed on this page (e.g. from ProductCard)
  useEffect(() => {
    const onCartUpdated = () => {
      if (isAuthenticated) fetchCart();
    };
    window.addEventListener('cartUpdated', onCartUpdated);
    return () => window.removeEventListener('cartUpdated', onCartUpdated);
  }, [isAuthenticated]);

  // Variant was deleted by admin: no size/variant, or variant_id points to missing variant
  const isItemVariantMissing = (item) => {
    if (item.item_type === 'combo') {
      const details = item.combo_selections_detail || [];
      return details.some((d) => !d.variant);
    }
    if (item.variant_id == null) return true;
    if (item.size == null && item.color_name == null) return true;
    return false;
  };

  const getUnavailableReason = (item) => {
    if (item.item_type === 'combo') {
      return item.combo_unavailable_reason || 'Out of stock';
    }
    if (item.product_unavailable || item.is_active === 0 || item.is_active === false || item.is_active === '0') {
      return item.product_unavailable_reason || 'Product not available at the moment';
    }
    const stockRaw = item.stock_quantity;
    if (stockRaw == null || stockRaw === '') return null;
    const stock = parseInt(stockRaw, 10);
    if (Number.isNaN(stock)) return null;
    const qty = parseInt(item.quantity, 10) || 0;
    if (stock === 0) return 'Out of stock';
    if (qty > stock) return `Only ${stock} item${stock === 1 ? '' : 's'} left in stock`;
    return null;
  };

  // Item is unavailable when inactive or out of stock
  const isItemUnavailable = (item) => {
    if (item.item_type === 'combo') {
      return !item.combo_in_stock;
    }
    if (item.product_unavailable || item.is_active === 0 || item.is_active === false || item.is_active === '0') {
      return true;
    }
    const stockRaw = item.stock_quantity;
    if (stockRaw == null || stockRaw === '') return false;
    const stock = parseInt(stockRaw, 10);
    if (Number.isNaN(stock)) return false;
    const qty = parseInt(item.quantity, 10) || 0;
    if (stock === 0) return true;
    return qty > stock;
  };

  const variantMissingItems = cartItems.filter(isItemVariantMissing);
  const unavailableItems = cartItems.filter(isItemUnavailable);
  const comboOnlyItems = cartItems.filter((item) => item.combo_only);
  const hasVariantMissingItems = variantMissingItems.length > 0;
  const hasUnavailableItems = unavailableItems.length > 0;
  const hasComboOnlyItems = comboOnlyItems.length > 0;
  const hasBlockingCartIssues = hasVariantMissingItems || hasUnavailableItems;

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cart');
      setCartItems(response.cart || []);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(itemId);
      return;
    }

    try {
      await api.put(`/cart/${itemId}`, { quantity: newQuantity });
      fetchCart();
      // Trigger cart count update in navbar
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to update quantity';
      toast.error(msg);
    }
  };

  const fetchBestsellers = async () => {
    try {
      const response = await api.get('/products/bestsellers');
      setBestsellers(response.products || []);
    } catch (error) {
      console.error('Error fetching bestsellers:', error);
    }
  };

  const removeItem = async (itemId) => {
    try {
      await api.delete(`/cart/${itemId}`);
      toast.success('Item removed from cart');
      fetchCart();
      // Trigger cart count update in navbar
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const calculateSubtotal = () => {
    // Use price from items
    return cartItems.reduce((total, item) => {
      const basePrice = parseFloat(item.price) || 0;
      return total + basePrice * (parseInt(item.quantity) || 0);
    }, 0);
  };
  const handleShowMore = () => {
    navigate('/products');
  }
  const calculateTotalTax = () => {
    return cartItems.reduce((total, item) => {
      const basePrice = parseFloat(item.price) || 0;
      const taxGst = parseFloat(item.tax_gst) || 0;
      const quantity = parseInt(item.quantity) || 0;
      const itemGst = (basePrice * taxGst / 100) * quantity;
      return total + itemGst;
    }, 0);
  };

  const calculateTotal = () => {
    // Merchandise + GST only — delivery is calculated at checkout from weight + zone
    return calculateSubtotal() + calculateTotalTax();
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    if (!isAuthenticated) {
      toast.info('Please login to checkout');
      navigate('/login');
      return;
    }
    if (hasVariantMissingItems) {
      toast.error('Some items have a removed size/variant. Please remove them and add again with the right size and color.');
      return;
    }
    if (hasComboOnlyItems) {
      toast.error(
        comboOnlyItems[0]?.product_unavailable_reason ||
          'Some items are combo-only and must be purchased as a combo. Please remove them from your cart.'
      );
      return;
    }
    if (hasUnavailableItems) {
      toast.error('Some items are unavailable. Please remove them and add the product again from the product page.');
      return;
    }
    navigate('/checkout');
  };

  if (loading) return null;

  return (
    <Container className="cart-page">
      <h1 className="cart-title">Your Shopping Cart</h1>
        {hasVariantMissingItems && (
            <Alert variant="warning" className="cart-unavailable-alert mb-4">
              <strong>Some items have a removed size/variant.</strong> Please remove them and add again with the right size and color from the product page.
            </Alert>
          )}
        {hasComboOnlyItems && !hasVariantMissingItems && (
            <Alert variant="warning" className="cart-unavailable-alert mb-4">
              <strong>Some items are combo-only.</strong>{' '}
              {comboOnlyItems.map((item) => item.product_unavailable_reason).filter(Boolean)[0] ||
                'These products cannot be purchased separately. Remove them from your cart and buy the combo instead.'}
            </Alert>
          )}
        {hasUnavailableItems && !hasVariantMissingItems && !hasComboOnlyItems && (
            <Alert variant="warning" className="cart-unavailable-alert mb-4">
              <strong>Some items in your cart are unavailable</strong> (out of stock or not available at the moment). Please remove them before checkout.
            </Alert>
          )}
      {cartItems.length === 0 ? (
        <div className="empty-cart">
          <h2 className="empty-cart-title">Your cart is empty</h2>
          <p className="empty-cart-text">Start shopping to add items to your cart.</p>
          <Link to="/products">
            <Button className="checkout-btn" style={{ maxWidth: '300px' }}>
              Browse Products
            </Button>
          </Link>
        </div>
      ) : (
        
        <div className="cart-layout">
        

          {/* Left Side - Cart Items */}
          <div className="cart-items-section">
            {cartItems.map((item) => {
              const isCombo = item.item_type === 'combo';
              const basePrice = parseFloat(item.price) || 0;
              const finalPrice = parseFloat(item.final_price) || 0;
              const taxGst = parseFloat(item.tax_gst) || 0;
              const quantity = parseInt(item.quantity) || 1;

              const unitFinalPrice = isCombo
                ? basePrice
                : finalPrice > 0
                  ? finalPrice
                  : basePrice + (basePrice * taxGst) / 100;
              const itemTotal = unitFinalPrice * quantity;

              let variantString = '';
              if (isCombo && item.combo_selections_detail?.length) {
                variantString = item.combo_selections_detail
                  .map((d) => {
                    const parts = [];
                    if (d.size) parts.push(d.size);
                    if (d.color_name) parts.push(d.color_name);
                    return `${d.product_name}${parts.length ? ` (${parts.join(', ')})` : ''}`;
                  })
                  .join(' + ');
              } else {
                const variantInfo = [];
                if (item.size) variantInfo.push(item.size);
                if (item.color_name) variantInfo.push(item.color_name);
                variantString = variantInfo.length > 0 ? ` (${variantInfo.join(', ')})` : '';
              }

              const variantMissing = isItemVariantMissing(item);
              const unavailable = isItemUnavailable(item);
              const showIssue = variantMissing || unavailable;
              const itemLink = isCombo ? `/combos/${item.combo_id}` : `/products/${item.product_id}`;
              const strikePrice = parseFloat(item.strike_through_price) || 0;
              const discountPercent =
                strikePrice > unitFinalPrice
                  ? Math.round(((strikePrice - unitFinalPrice) / strikePrice) * 100)
                  : 0;
              const comboDetails = item.combo_selections_detail || [];

              if (isCombo) {
                return (
                  <div key={item.id} className={`cart-item-card cart-combo-card ${showIssue ? 'cart-item-unavailable' : ''}`}>
                    <div className="cart-combo-top">
                      <Link to={itemLink} className="cart-item-image-link">
                        <div className="cart-item-image">
                          {item.product_image ? (
                            <img src={item.product_image} alt={item.product_name} />
                          ) : null}
                          <span className="cart-combo-badge">COMBO</span>
                          {variantMissing && <span className="cart-item-unavailable-badge">Variant removed</span>}
                          {unavailable && !variantMissing && (
                            <span className="cart-item-unavailable-badge">
                              {item.combo_only ? 'Combo only' : 'Unavailable'}
                            </span>
                          )}
                        </div>
                      </Link>

                      <div className="cart-item-details">
                        <Link to={itemLink} className="cart-item-name-link">
                          <div className="cart-item-name">{item.product_name}</div>
                        </Link>
                        <div className="cart-combo-subtitle">
                          {item.combo_includes_label ||
                            comboDetails.map((d) => d.product_name).filter(Boolean).join(' + ') ||
                            '2 Items'}
                        </div>
                        <div className="cart-combo-pricing">
                          <span className="cart-combo-price">₹{unitFinalPrice.toFixed(2)}</span>
                          {strikePrice > unitFinalPrice && (
                            <span className="cart-combo-strike">₹{strikePrice.toFixed(2)}</span>
                          )}
                        </div>
                        {variantMissing && (
                          <div className="cart-item-unavailable-msg">This item&apos;s size/variant was removed. Remove it and add again with the right size and color.</div>
                        )}
                        {unavailable && !variantMissing && (
                          <div className="cart-item-unavailable-msg small text-muted">
                            {getUnavailableReason(item) ||
                              'Remove and add again from product page to check stock.'}
                          </div>
                        )}
                      </div>

                      <div className="cart-item-actions">
                        <div className="cart-quantity-controls">
                          <button
                            className="cart-quantity-btn"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          >
                            -
                          </button>
                          <input
                            type="number"
                            className="cart-quantity-input"
                            value={item.quantity}
                            onChange={(e) => {
                              const newQty = parseInt(e.target.value) || 1;
                              updateQuantity(item.id, newQty);
                            }}
                            min={1}
                          />
                          <button
                            className="cart-quantity-btn"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          >
                            +
                          </button>
                        </div>
                        <button
                          className="cart-delete-btn"
                          onClick={() => removeItem(item.id)}
                          title="Remove item"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {comboDetails.length > 0 && (
                      <div className="cart-combo-includes">
                        <div className="cart-combo-includes-title">
                          This combo includes ({comboDetails.length} Item{comboDetails.length === 1 ? '' : 's'})
                        </div>
                        <div className="cart-combo-includes-grid">
                          {comboDetails.map((d, idx) => (
                            <div key={d.variant_id || idx} className="cart-combo-include-card">
                              <div className="cart-combo-include-image">
                                {d.product_image ? <img src={d.product_image} alt={d.product_name} /> : null}
                              </div>
                              <div className="cart-combo-include-info">
                                <div className="cart-combo-include-name">{d.product_name}</div>
                                <div className="cart-combo-include-variant">
                                  {[d.color_name, d.size].filter(Boolean).join(' • ') || '—'}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div key={item.id} className={`cart-item-card ${showIssue ? 'cart-item-unavailable' : ''}`}>
                  <Link to={itemLink} className="cart-item-image-link">
                    <div className="cart-item-image">
                      {item.product_image ? (
                        <img src={item.product_image} alt={item.product_name} />
                      ) : null}
                      {variantMissing && <span className="cart-item-unavailable-badge">Variant removed</span>}
                      {unavailable && !variantMissing && (
                        <span className="cart-item-unavailable-badge">
                          {item.combo_only ? 'Combo only' : 'Unavailable'}
                        </span>
                      )}
                    </div>
                  </Link>

                  <div className="cart-item-details">
                    <Link to={itemLink} className="cart-item-name-link">
                      <div className="cart-item-name">
                        {item.product_name}
                        {variantString}
                      </div>
                    </Link>
                    {variantMissing && (
                      <div className="cart-item-unavailable-msg">This item&apos;s size/variant was removed. Remove it and add again with the right size and color.</div>
                    )}
                    {unavailable && !variantMissing && (
                      <div className="cart-item-unavailable-msg small text-muted">
                        {getUnavailableReason(item) ||
                          'Remove and add again from product page to check stock.'}
                      </div>
                    )}
                    <div className="cart-item-price justify-start items-start">
                      <div className="fw-bold mt-1 d-flex align-items-baseline flex-wrap gap-2">
                        <span>₹{itemTotal.toFixed(2)}</span>
                        {strikePrice > unitFinalPrice && (
                          <>
                            <span className="cart-combo-strike">₹{(strikePrice * quantity).toFixed(2)}</span>
                            {((item.is_sale === 1 || item.is_sale === true) ||
                              (parseFloat(item.discount_percentage) || 0) > 0) &&
                              discountPercent > 0 && (
                              <span className="cart-combo-off">({discountPercent}% OFF)</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="cart-item-actions">
                    <div className="cart-quantity-controls">
                      <button
                        className="cart-quantity-btn"
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        className="cart-quantity-input"
                        value={item.quantity}
                        onChange={(e) => {
                          const newQty = parseInt(e.target.value) || 1;
                          updateQuantity(item.id, newQty);
                        }}
                        min={1}
                      />
                      <button
                        className="cart-quantity-btn"
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                    <button
                      className="cart-delete-btn"
                      onClick={() => removeItem(item.id)}
                      title="Remove item"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Side - Order Summary */}
          <div className="order-summary">
            <h3 className="order-summary-title">Order Summary</h3>
            <div className="order-summary-row">
              <span className="order-summary-label">Subtotal (Items)</span>
              <span className="order-summary-value">₹{calculateSubtotal().toFixed(2)}</span>
            </div>
            <div className="order-summary-row">
              <span className="order-summary-label">GST/Tax</span>
              <span className="order-summary-value">₹{calculateTotalTax().toFixed(2)}</span>
            </div>
            <div className="order-summary-row">
              <span className="order-summary-label">Shipping</span>
              <span className="order-summary-value">Calculated at checkout</span>
            </div>
            <div className="order-summary-row total">
              <span className="order-summary-label">Grand Total</span>
              <span className="order-summary-value">₹{calculateTotal().toFixed(2)}</span>
            </div>
            <Button
              className="checkout-btn"
              onClick={handleCheckout}
              disabled={hasBlockingCartIssues}
            >
             PROCEED TO CHECKOUT
            </Button>
          </div>
        </div>
      )}

      {/* Bestsellers Section */}
      {bestsellers.length > 0 && (
        <div className="bestsellers-section mt-5">
          <h2 className="section-title mb-8">Bestsellers</h2>
          <Row>
            {bestsellers.map((product) => (
              <Col key={product.id} md={4} sm={6} xs={6} lg={3} className="mb-4">
                <ProductCard product={product} buttonText="Add to Cart" />
              </Col>
            ))}
          </Row>
           <div className="w-100 flex align-items-center justify-content-center">

      <button onClick={handleShowMore} className="load-more-btn my-4">Show More</button>
      </div>
        </div>
      )}
    </Container>
  );
};

export default Cart;

